'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Heading, Text } from '../../components/index.js'
import { CreditsShelfContext, type CreditsShelfContextValue } from './context.js'
import { useExposure } from '../../hooks/useExposure.js'

const componentType = 'shelf'
const componentName = 'credits_shelf'

export interface CreditsShelfRootProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 主标题 */
  title?: string
  /** 副标题 */
  subtitle?: string
  /** 主题 */
  theme?: 'light' | 'dark'
  /** 文案配置 */
  copy?: CreditsShelfContextValue['copy']
  /** 是否展示标签 */
  showTags?: boolean
  /** 是否展示原价 */
  showOriginalPrice?: boolean
  /** 语义化类名 */
  classNames?: CreditsShelfContextValue['classNames']
  /** 次要按钮文案 */
  secondaryButtonText?: string
  /** 次要按钮功能 */
  secondaryButtonFun?: CreditsShelfContextValue['secondaryButtonFun']
  /** 主要按钮文案 */
  primaryButtonText?: string
  /** 主要按钮功能 */
  primaryButtonFun?: CreditsShelfContextValue['primaryButtonFun']
  /** 了解更多回调 */
  onLearnMore?: CreditsShelfContextValue['onLearnMore']
  /** 立即购买回调 */
  onShopNow?: CreditsShelfContextValue['onShopNow']
  /** 加入购物车回调 */
  onAddToCart?: CreditsShelfContextValue['onAddToCart']
  /** 产品图片点击回调 */
  onProductImageClick?: CreditsShelfContextValue['onProductImageClick']
  /** 子组件 */
  children: React.ReactNode
}

/**
 * CreditsShelf Root 组件
 *
 * @description 组合式组件的容器，提供 Context 配置
 */
const CreditsShelfRoot = React.forwardRef<HTMLDivElement, CreditsShelfRootProps>(
  (
    {
      title,
      subtitle,
      theme = 'light',
      copy,
      showTags,
      showOriginalPrice,
      classNames,
      secondaryButtonText,
      secondaryButtonFun,
      primaryButtonText,
      primaryButtonFun,
      onLearnMore,
      onShopNow,
      onAddToCart,
      onProductImageClick,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const boxRef = React.useRef<HTMLDivElement>(null)

    useExposure(boxRef, {
      componentType,
      componentName,
      componentTitle: title,
      componentDescription: subtitle,
    })

    React.useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

    const contextValue = React.useMemo<CreditsShelfContextValue>(
      () => ({
        theme,
        copy,
        showTags,
        showOriginalPrice,
        classNames,
        secondaryButtonText,
        secondaryButtonFun,
        primaryButtonText,
        primaryButtonFun,
        onLearnMore,
        onShopNow,
        onAddToCart,
        onProductImageClick,
      }),
      [
        theme,
        copy,
        showTags,
        showOriginalPrice,
        classNames,
        secondaryButtonText,
        secondaryButtonFun,
        primaryButtonText,
        primaryButtonFun,
        onLearnMore,
        onShopNow,
        onAddToCart,
        onProductImageClick,
      ]
    )

    return (
      <CreditsShelfContext.Provider value={contextValue}>
        <div
          ref={boxRef}
          className={cn(
            'text-info-primary w-full',
            {
              'aiui-dark': theme === 'dark',
            },
            className,
            classNames?.root
          )}
          {...props}
        >
          {/* 标题区域 */}
          {(title || subtitle) && (
            <div className={cn('mb-6', classNames?.header)}>
              {title && (
                <Heading as="h2" size={4} className={cn('text-info-primary mb-2', classNames?.title)} html={title} />
              )}
              {subtitle && <Text as="p" size={3} className={cn('', classNames?.subtitle)} html={subtitle} />}
            </div>
          )}

          {children}
        </div>
      </CreditsShelfContext.Provider>
    )
  }
)

CreditsShelfRoot.displayName = 'CreditsShelf.Root'

export { CreditsShelfRoot }
